import { Component, OnDestroy, ViewChild } from '@angular/core';
import { HardwareApiService } from 'src/app/services/api/hardware-api.service';
import { HardwareDialogComponent } from 'src/app/components/dialogs/hardware-dialog/hardware-dialog.component';
import { DialogSize } from 'src/app/models_new/enums/dialogSize';
import { SimulationWizardDataService } from '../../simulation-wizard-data.service';
import {
  combineLatest,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  take,
} from 'rxjs';
import { DialogService } from 'src/app/services/dialog.service';
import {
  ITableData,
  TableComponent,
} from '../../../../gui/table/table.component';
import { AssetApiService } from 'src/app/services/api/asset-api.service';
import { DataRequestState } from 'src/app/data-request/model';
import { toRequestState } from 'src/app/data-request/operators';
import { HardwareUtils } from '../../../../../utils/hardware-utils';
import { NotificationService } from 'src/app/services/notification.service';
import { skipWhile, startWith, takeUntil } from 'rxjs/operators';

type SelectActions = 'edit' | 'delete';

@Component({
  selector: 'app-select-hardware',
  templateUrl: './select-hardware.component.html',
  styleUrls: ['./select-hardware.component.scss'],
})
export class SelectHardwareComponent implements OnDestroy {
  hwTableData$: Observable<DataRequestState<ITableData<SelectActions>[]>>;
  columnsToDisplay: string[] = [
    'select',
    'name',
    'robot',
    'gripper',
    'lift_column',
    'height_extender',
    'frame',
    'conveyor',
  ];
  noDataHint: string;
  destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  @ViewChild('table') table: TableComponent<SelectActions>;

  constructor(
    private dialogService: DialogService,
    public simWizardDataService: SimulationWizardDataService,
    private assetApi: AssetApiService,
    private hwApi: HardwareApiService,
    private notify: NotificationService
  ) {
    this.hwTableData$ = combineLatest([
      this.simWizardDataService.hardwares$,
      this.hwApi.fetchHwTypes(),
      // Retrigger the table data when dual product mode changes.
      this.simWizardDataService.zimWizard.dualProductMode.valueChanges.pipe(
        // Start with the current value to trigger the initial fetch.
        startWith(this.simWizardDataService.zimWizard.dualProductMode.value)
      ),
    ]).pipe(
      map(([hw_configs, hw_types, dualProduct]) => {
        this.noDataHint = dualProduct
          ? `You have no Hardware configurations with 2 conveyors available.
          You must make a Hardware configuration with 2 conveyors to simulate with dual products.`
          : `You have no single conveyor Hardware configurations available, please create some.`;

        let tableData: ITableData<SelectActions>[] = [];
        for (let config of hw_configs) {
          if (dualProduct && config.data.conveyors.length < 2) {
            // If dual product mode, skip single-conveyor hardware configurations.
            continue;
          } else if (!dualProduct && config.data.conveyors.length > 1) {
            // If not dual product mode, skip dual-conveyor hardware configurations.
            continue;
          }
          HardwareUtils.labelifyScene(config.data, hw_types);

          tableData.push({
            data: {
              id: config.id,
              name: config.name,
              gripper: config.data.robot.gripper.label,
              frame: config.data.robot.frame.label,
              conveyor: config.data.conveyors[0].label,
              lift_column: config.data.robot.lift_kit.label,
              height_extender: config.data.robot_mounting_height.position.z,
              robot: config.data.robot.label,
              hardware: config,
            },
            actions: [
              {
                label: 'Edit',
                icon: 'edit',
                actionId: 'edit',
              },
              {
                label: 'Delete',
                icon: 'delete',
                actionId: 'delete',
                txtColor: '#AF2B2B',
              },
            ],
          });
        }
        return tableData;
      }),
      takeUntil(this.destroy$),
      toRequestState()
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public actionClicked(event): void {
    if (event.actionId === 'edit') {
      this.editCallback(event.element);
    } else if (event.actionId === 'delete') {
      this.deleteCallback(event.element);
    }
  }

  private editCallback(hw_config: any): void {
    let patternDialog = this.dialogService.showCustomDialog(
      HardwareDialogComponent,
      DialogSize.LARGE,
      null,
      {
        hw_id: hw_config.id,
        dualProductMode:
          this.simWizardDataService.zimWizard.dualProductMode.value,
      }
    );
    patternDialog.afterClosed().pipe(take(1)).subscribe();
  }

  private deleteCallback(hw_config: any): void {
    this.notify
      .deletePrompt('Delete', 'hardware configuration', [hw_config.name])
      .afterDismissed()
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() => {
          return this.deleteScene([hw_config.id]);
        })
      )
      .pipe(take(1))
      .subscribe(() => {
        this.table.selection.clear();
        this.notify.showMessage(
          `The hardware configuration were deleted successfully!`
        );
      });
  }

  private deleteScene(ids: string[]): Observable<any> {
    const operations = {};
    for (let i = 0; i < ids.length; i++) {
      operations[i] = this.hwApi
        .deleteSceneById(ids[i])
        .pipe(
          switchMap((response) =>
            response.image
              ? this.assetApi.deleteAsset(response.image.id)
              : of(response)
          )
        );
    }
    return forkJoin(operations);
  }

  public createHwConfig(): void {
    let hardwareDialog = this.dialogService.showCustomDialog(
      HardwareDialogComponent,
      DialogSize.LARGE,
      null,
      null,
      true
    );
    hardwareDialog
      .afterClosed()
      .pipe(
        skipWhile((hw) => !hw),
        take(1)
      )
      .subscribe(([_all, _added]) => {
        this.notify.showSuccess('New hardware config added!');
      });
  }
}
